import { useState } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';

/**
 * Enhanced Date Selector Component
 * Provides granular date filtering with Year, Quarter, and Month options
 * 
 * @param {Function} onDateRangeChange - Callback when date range changes, receives {startDate, endDate, label}
 * @param {Array} availableYears - Array of years available in the data
 * @param {string} className - Additional CSS classes
 */
const EnhancedDateSelector = ({ onDateRangeChange, availableYears = [], className = '' }) => {
  const [granularity, setGranularity] = useState('all'); // 'all', 'year', 'quarter', 'month'
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedQuarter, setSelectedQuarter] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');

  const quarters = [
    { value: 'Q1', label: 'Q1 (Jan-Mar)', months: [0, 1, 2] },
    { value: 'Q2', label: 'Q2 (Apr-Jun)', months: [3, 4, 5] },
    { value: 'Q3', label: 'Q3 (Jul-Sep)', months: [6, 7, 8] },
    { value: 'Q4', label: 'Q4 (Oct-Dec)', months: [9, 10, 11] },
  ];

  const months = [
    { value: '0', label: 'January' },
    { value: '1', label: 'February' },
    { value: '2', label: 'March' },
    { value: '3', label: 'April' },
    { value: '4', label: 'May' },
    { value: '5', label: 'June' },
    { value: '6', label: 'July' },
    { value: '7', label: 'August' },
    { value: '8', label: 'September' },
    { value: '9', label: 'October' },
    { value: '10', label: 'November' },
    { value: '11', label: 'December' },
  ];

  // Calculate date range based on selections
  const calculateDateRange = (gran, year, quarter, month) => {
    if (gran === 'all') {
      onDateRangeChange({ startDate: null, endDate: null, label: 'All Time' });
      return;
    }

    if (!year) return;

    const yearNum = parseInt(year);
    let startDate, endDate, label;

    if (gran === 'year') {
      startDate = new Date(yearNum, 0, 1); // Jan 1
      endDate = new Date(yearNum, 11, 31, 23, 59, 59); // Dec 31
      label = `Year ${year}`;
    } else if (gran === 'quarter' && quarter) {
      const q = quarters.find(q => q.value === quarter);
      if (q) {
        startDate = new Date(yearNum, q.months[0], 1);
        endDate = new Date(yearNum, q.months[2] + 1, 0, 23, 59, 59); // Last day of quarter
        label = `${q.value} ${year}`;
      }
    } else if (gran === 'month' && month) {
      const monthNum = parseInt(month);
      startDate = new Date(yearNum, monthNum, 1);
      endDate = new Date(yearNum, monthNum + 1, 0, 23, 59, 59); // Last day of month
      label = `${months[monthNum].label} ${year}`;
    }

    if (startDate && endDate) {
      onDateRangeChange({ startDate, endDate, label });
    }
  };

  const handleGranularityChange = (value) => {
    setGranularity(value);
    if (value === 'all') {
      setSelectedYear('');
      setSelectedQuarter('');
      setSelectedMonth('');
      calculateDateRange('all');
    } else if (value === 'quarter') {
      setSelectedMonth('');
    } else if (value === 'month') {
      setSelectedQuarter('');
    }
  };

  const handleYearChange = (value) => {
    setSelectedYear(value);
    if (granularity === 'year') {
      calculateDateRange('year', value);
    }
  };

  const handleQuarterChange = (value) => {
    setSelectedQuarter(value);
    if (selectedYear) {
      calculateDateRange('quarter', selectedYear, value);
    }
  };

  const handleMonthChange = (value) => {
    setSelectedMonth(value);
    if (selectedYear) {
      calculateDateRange('month', selectedYear, null, value);
    }
  };

  const handleReset = () => {
    setGranularity('all');
    setSelectedYear('');
    setSelectedQuarter('');
    setSelectedMonth('');
    calculateDateRange('all');
  };

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {/* Date Range Icon */}
      <div className="flex items-center gap-2 text-sm text-slate-600">
        <Calendar className="w-5 h-5" />
        <span className="font-medium">Date Range:</span>
      </div>

      {/* Granularity Selector */}
      <Select value={granularity} onValueChange={handleGranularityChange}>
        <SelectTrigger className="w-40 bg-white border-slate-300 hover:border-blue-500 transition-colors">
          <SelectValue placeholder="Select period" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Time</SelectItem>
          <SelectItem value="year">By Year</SelectItem>
          <SelectItem value="quarter">By Quarter</SelectItem>
          <SelectItem value="month">By Month</SelectItem>
        </SelectContent>
      </Select>

      {/* Year Selector (shown for year, quarter, month) */}
      {granularity !== 'all' && (
        <Select value={selectedYear} onValueChange={handleYearChange}>
          <SelectTrigger className="w-32 bg-white border-slate-300 hover:border-blue-500 transition-colors">
            <SelectValue placeholder="Year" />
          </SelectTrigger>
          <SelectContent>
            {availableYears.length > 0 ? (
              availableYears.map((year) => (
                <SelectItem key={year} value={year.toString()}>
                  {year}
                </SelectItem>
              ))
            ) : (
              <SelectItem value="2024">2024</SelectItem>
            )}
          </SelectContent>
        </Select>
      )}

      {/* Quarter Selector (shown only for quarter) */}
      {granularity === 'quarter' && selectedYear && (
        <Select value={selectedQuarter} onValueChange={handleQuarterChange}>
          <SelectTrigger className="w-44 bg-white border-slate-300 hover:border-blue-500 transition-colors">
            <SelectValue placeholder="Select Quarter" />
          </SelectTrigger>
          <SelectContent>
            {quarters.map((q) => (
              <SelectItem key={q.value} value={q.value}>
                {q.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Month Selector (shown only for month) */}
      {granularity === 'month' && selectedYear && (
        <Select value={selectedMonth} onValueChange={handleMonthChange}>
          <SelectTrigger className="w-44 bg-white border-slate-300 hover:border-blue-500 transition-colors">
            <SelectValue placeholder="Select Month" />
          </SelectTrigger>
          <SelectContent>
            {months.map((m) => (
              <SelectItem key={m.value} value={m.value}>
                {m.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Reset Button (shown when not 'all') */}
      {granularity !== 'all' && (
        <Button
          onClick={handleReset}
          variant="outline"
          className="border-slate-300 hover:border-red-500 hover:bg-red-50 hover:text-red-600 transition-colors"
        >
          Reset
        </Button>
      )}
    </div>
  );
};

export default EnhancedDateSelector;
